import React from "react";
import {
  Box,
  Typography,
  Link as MuiLink,
  Stack,
  Divider,
  Container,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import MailIcon from "@mui/icons-material/Mail";
import logo from "../Image/9rlogo.png";

// Social media links (reused from Navbar)
const socialLinks = [
  {
    href: "https://api.whatsapp.com/send?phone=+919925615191&text=Hi+9|R,+I+want+some+more+information+about+your+services.+Please+reply+soon.",
    icon: <WhatsAppIcon />,
    color: "#25D366",
    label: "WhatsApp",
  },
  {
    href: "https://www.facebook.com/profile.php?id=61563096922232",
    icon: <FacebookIcon />,
    color: "#1877F2",
    label: "Facebook",
  },
  {
    href: "https://www.instagram.com/9rnfc_com?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
    icon: <InstagramIcon />,
    color: "#E4405F",
    label: "Instagram",
  },
  {
    href: "https://www.linkedin.com/in/9r-nfc-b63aaa319",
    icon: <LinkedInIcon />,
    color: "#0A66C2",
    label: "LinkedIn",
  },
  {
    href: "mailto:9rnfccard@gmail.com",
    icon: <MailIcon />,
    color: "#EA4335",
    label: "Email",
  },
];

// Quick links
const quickLinks = [
  { label: "Home", path: "/" },
  { label: "Email Sender", path: "/email-sender" },
  { label: "How to Use", path: "/how-to-use" },
  { label: "About", path: "/about" },
];

// Reusable Social Icon Component (assumed to be imported or defined in the same scope as Navbar)
const SocialIcon = ({ href, icon, color, label }) => (
  <IconButton
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={`Connect via ${label}`}
    sx={{
      color,
      background: `rgba(${parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)}, 0.08)`,
      borderRadius: 1.5,
      width: 40,
      height: 40,
      "&:hover": {
        background: `rgba(${parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)}, 0.2)`,
        transform: "scale(1.1)",
      },
      transition: "all 0.2s ease",
    }}
  >
    {icon}
  </IconButton>
);

const Footer = React.memo(() => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#f8fafc",
        borderTop: "1px solid rgba(0,0,0,0.08)",
        mt: "auto",
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            gap: { xs: 3, sm: 4, md: 6 },
            pb: { xs: 2, sm: 3 },
          }}
        >
          {/* Section 1: Company Info */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 2 }}
            alignItems={{ xs: "center", md: "flex-start" }}
            sx={{ minWidth: { md: "280px" }, textAlign: { xs: "center", md: "left" } }}
          >
            <Box
              component="img"
              src={logo}
              alt="Email Sender logo"
              loading="lazy"
              sx={{
                height: { xs: 36, sm: 40, md: 44 },
                width: { xs: 36, sm: 40, md: 44 },
                borderRadius: 1,
              }}
            />
            <Stack spacing={1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#1f2937",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                }}
              >
                Email Sender
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                  lineHeight: 1.6,
                  maxWidth: { md: "300px" },
                }}
              >
                Professional bulk email solution with advanced features for personalized campaigns.
              </Typography>
            </Stack>
          </Stack>

          {/* Section 2: Quick Links */}
          <Stack
            spacing={1.5}
            sx={{
              alignItems: { xs: "center", md: "flex-start" },
              minWidth: { md: "140px" },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
              }}
            >
              Quick Links
            </Typography>
            {quickLinks.map((link) => (
              <MuiLink
                key={link.path}
                component={Link}
                to={link.path}
                underline="hover"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                  color: "#2563eb",
                  fontFamily: "'Inter', sans-serif",
                  "&:hover": {
                    color: "#1d4ed8",
                    transform: "translateX(2px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {link.label}
              </MuiLink>
            ))}
          </Stack>

          {/* Section 3: Contact & Social */}
          <Stack
            spacing={1.5}
            sx={{
              alignItems: { xs: "center", md: "flex-start" },
              minWidth: { md: "220px" },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
              }}
            >
              Contact & Connect
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Connect with us on social media
            </Typography>
            <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "center", md: "flex-start" } }}>
              {socialLinks.map((link) => (
                <SocialIcon key={link.href} {...link} />
              ))}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                fontWeight: 500,
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Email: 9rnfccard@gmail.com
            </Typography>
          </Stack>
        </Box>

        {/* Divider */}
        <Divider sx={{ my: { xs: 2, sm: 3 }, borderColor: "rgba(0,0,0,0.08)" }} />

        {/* Copyright & Credits */}
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            pb: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
              fontWeight: 500,
            }}
          >
            Copyright © 2025. All rights reserved by{" "}
            <MuiLink
              href="https://9rnfc.com/"
              target="_blank"
              rel="noopener"
              sx={{
                fontWeight: 600,
                color: "#2563eb",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Liontouch Webpark PVT.
            </MuiLink>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
});

export default Footer;